var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect } from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";
import { Modal, Button, Input, Tree, Empty, Spin } from "antd";
var Search = Input.Search;
/**
 * 通用树形视图模态框组件
 * 参考 GenericModalForm 的设计模式，提供高度可配置的树形展示功能
 */
var GenericModalTree = function (_a) {
    var visible = _a.visible, title = _a.title, _b = _a.size, size = _b === void 0 ? {} : _b, onClose = _a.onClose, _c = _a.treeData, treeData = _c === void 0 ? [] : _c, _d = _a.searchConfig, searchConfig = _d === void 0 ? {} : _d, _e = _a.selectionConfig, selectionConfig = _e === void 0 ? {} : _e, _f = _a.actionConfig, actionConfig = _f === void 0 ? {} : _f, _g = _a.treeConfig, treeConfig = _g === void 0 ? {} : _g, _h = _a.modalProps, modalProps = _h === void 0 ? {} : _h, _j = _a.emptyText, emptyText = _j === void 0 ? "暂无数据" : _j, _k = _a.loadingText, loadingText = _k === void 0 ? "加载中..." : _k;
    // 默认尺寸配置
    var defaultSize = {
        width: 600,
        height: "60vh",
        maxWidth: "80vw",
        maxHeight: "80vh",
        minWidth: 400,
        minHeight: "40vh",
    };
    // 合并尺寸配置
    var modalSize = __assign(__assign({}, defaultSize), size);
    // 默认配置合并
    var _l = searchConfig || {}, _m = _l.enabled, searchEnabled = _m === void 0 ? true : _m, _o = _l.placeholder, searchPlaceholder = _o === void 0 ? "搜索..." : _o, onSearchCallback = _l.onSearch;
    var _p = selectionConfig || {}, _q = _p.enabled, selectionEnabled = _q === void 0 ? false : _q, _r = _p.mode, selectionMode = _r === void 0 ? "multiple" : _r, _s = _p.checkable, checkable = _s === void 0 ? false : _s, _t = _p.selectedKeys, externalSelectedKeys = _t === void 0 ? [] : _t, _u = _p.checkedKeys, externalCheckedKeys = _u === void 0 ? [] : _u, onSelectionChange = _p.onSelectionChange, onCheckChange = _p.onCheckChange;
    var _v = actionConfig || {}, _w = _v.showConfirm, showConfirm = _w === void 0 ? true : _w, _x = _v.showCancel, showCancel = _x === void 0 ? true : _x, _y = _v.showClose, showClose = _y === void 0 ? false : _y, _z = _v.confirmText, confirmText = _z === void 0 ? "确认" : _z, _0 = _v.cancelText, cancelText = _0 === void 0 ? "取消" : _0, _1 = _v.closeText, closeText = _1 === void 0 ? "关闭" : _1, onConfirm = _v.onConfirm, onCancel = _v.onCancel;
    var _2 = treeConfig || {}, _3 = _2.defaultExpandAll, defaultExpandAll = _3 === void 0 ? true : _3, _4 = _2.showLine, showLine = _4 === void 0 ? { showLeafIcon: false } : _4, _5 = _2.showIcon, showIcon = _5 === void 0 ? true : _5, _6 = _2.loading, loading = _6 === void 0 ? false : _6;
    // 内部状态
    var _7 = useState(""), searchValue = _7[0], setSearchValue = _7[1];
    var _8 = useState(defaultExpandAll ? getAllKeys(treeData) : []), expandedKeys = _8[0], setExpandedKeys = _8[1];
    var _9 = useState(externalSelectedKeys), internalSelectedKeys = _9[0], setInternalSelectedKeys = _9[1];
    var _10 = useState(externalCheckedKeys), internalCheckedKeys = _10[0], setInternalCheckedKeys = _10[1];
    var _11 = useState(true), autoExpandParent = _11[0], setAutoExpandParent = _11[1];
    // 获取所有节点的key（用于默认展开）
    function getAllKeys(nodes) {
        var keys = [];
        var traverse = function (nodeList) {
            nodeList.forEach(function (node) {
                keys.push(node.key);
                if (node.children) {
                    traverse(node.children);
                }
            });
        };
        traverse(nodes);
        return keys;
    }
    // 同步外部状态
    useEffect(function () {
        if (selectionEnabled) {
            setInternalSelectedKeys(externalSelectedKeys);
        }
        if (checkable) {
            setInternalCheckedKeys(externalCheckedKeys);
        }
    }, [externalSelectedKeys, externalCheckedKeys, selectionEnabled, checkable]);
    // 重置状态
    useEffect(function () {
        if (visible) {
            setSearchValue("");
            // 根据defaultExpandAll设置展开状态
            if (defaultExpandAll) {
                setExpandedKeys(getAllKeys(treeData));
            }
            else {
                setExpandedKeys([]);
            }
            setAutoExpandParent(true);
        }
    }, [visible, defaultExpandAll, treeData]);
    // 处理搜索
    var handleSearch = function (value) {
        setSearchValue(value);
        if (onSearchCallback) {
            onSearchCallback(value);
        }
        // 自动展开匹配的节点
        if (value) {
            var matchedKeys_1 = new Set();
            var parentKeysToExpand_1 = new Set();
            var getMatchedKeys_1 = function (nodes, parent) {
                nodes.forEach(function (node) {
                    var _a;
                    var nodeWithParent = __assign(__assign({}, node), { parent: parent });
                    var titleText = typeof node.title === "string"
                        ? node.title
                        : ((_a = node.title) === null || _a === void 0 ? void 0 : _a.toString()) || "";
                    if (titleText.toLowerCase().includes(value.toLowerCase())) {
                        matchedKeys_1.add(node.key);
                        // 添加父节点到展开列表
                        var currentParent = parent;
                        while (currentParent) {
                            parentKeysToExpand_1.add(currentParent.key);
                            currentParent = currentParent.parent;
                        }
                    }
                    if (node.children) {
                        getMatchedKeys_1(node.children, nodeWithParent);
                    }
                });
            };
            getMatchedKeys_1(treeData);
            setExpandedKeys(Array.from(parentKeysToExpand_1));
            setAutoExpandParent(true);
        }
        else {
            setExpandedKeys([]);
            setAutoExpandParent(false);
        }
    };
    // 处理节点选择
    var handleSelect = function (selectedKeys, info) {
        if (selectionEnabled && onSelectionChange) {
            var keys = selectionMode === "single" ? selectedKeys.slice(-1) : selectedKeys;
            setInternalSelectedKeys(keys);
            onSelectionChange(keys);
        }
    };
    // 处理复选框变化
    var handleCheck = function (checkedKeys, info) {
        if (checkable && onCheckChange) {
            var keys = Array.isArray(checkedKeys)
                ? checkedKeys
                : checkedKeys.checked || [];
            setInternalCheckedKeys(keys);
            onCheckChange(keys);
        }
    };
    // 处理确认
    var handleConfirm = function () {
        if (onConfirm) {
            var keysToConfirm = checkable
                ? internalCheckedKeys
                : internalSelectedKeys;
            onConfirm(keysToConfirm);
        }
        handleClose();
    };
    // 处理取消
    var handleCancel = function () {
        if (onCancel) {
            onCancel();
        }
        handleClose();
    };
    // 处理关闭
    var handleClose = function () {
        // 重置选择状态
        setInternalCheckedKeys([]);
        setInternalSelectedKeys([]);
        setExpandedKeys([]);
        setSearchValue("");
        onClose();
    };
    // 过滤树形数据
    var filteredTreeData = useMemo(function () {
        if (!searchValue)
            return treeData;
        var filterTree = function (nodes) {
            return nodes.reduce(function (acc, node) {
                var _a;
                var titleText = typeof node.title === "string"
                    ? node.title
                    : ((_a = node.title) === null || _a === void 0 ? void 0 : _a.toString()) || "";
                var matches = titleText
                    .toLowerCase()
                    .includes(searchValue.toLowerCase());
                var children = node.children ? filterTree(node.children) : [];
                if (matches || children.length > 0) {
                    acc.push(__assign(__assign({}, node), { children: children.length > 0 ? children : node.children }));
                }
                return acc;
            }, []);
        };
        return filterTree(treeData);
    }, [treeData, searchValue]);
    // 渲染操作按钮
    var renderFooter = function () {
        var buttons = [];
        if (showCancel) {
            buttons.push(_jsx(Button, __assign({ onClick: handleCancel }, { children: cancelText }), "cancel"));
        }
        if (showConfirm && selectionEnabled) {
            buttons.push(_jsx(Button, __assign({ type: "primary", onClick: handleConfirm }, { children: confirmText }), "confirm"));
        }
        if (showClose && !selectionEnabled) {
            buttons.push(_jsx(Button, __assign({ onClick: onClose }, { children: closeText }), "close"));
        }
        return buttons.length > 0 ? buttons : null;
    };
    // 渲染内容
    var renderContent = function () {
        if (loading) {
            return (_jsxs("div", __assign({ style: {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                    gap: 16,
                } }, { children: [_jsx(Spin, { size: "large" }), _jsx("span", __assign({ style: { color: "#666" } }, { children: loadingText }))] })));
        }
        if (!treeData || treeData.length === 0) {
            return (_jsx("div", __assign({ style: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                } }, { children: _jsx(Empty, { description: emptyText }) })));
        }
        return (_jsx(Tree, { treeData: filteredTreeData, checkable: checkable, selectable: selectionEnabled, selectedKeys: selectionEnabled ? internalSelectedKeys : undefined, checkedKeys: checkable ? internalCheckedKeys : undefined, onSelect: selectionEnabled ? handleSelect : undefined, onCheck: checkable ? handleCheck : undefined, expandedKeys: expandedKeys, onExpand: function (keys) {
                setExpandedKeys(keys);
                setAutoExpandParent(false);
            }, autoExpandParent: autoExpandParent, defaultExpandAll: defaultExpandAll, showLine: showLine, showIcon: showIcon, titleRender: function (nodeData) { return nodeData.title; }, filterTreeNode: function (node) {
                var _a;
                if (!searchValue)
                    return true;
                var titleText = typeof node.title === "string"
                    ? node.title
                    : ((_a = node.title) === null || _a === void 0 ? void 0 : _a.toString()) || "";
                return titleText.toLowerCase().includes(searchValue.toLowerCase());
            } }));
    };
    return (_jsx(Modal, __assign({ title: title, open: visible, width: modalSize.width, onCancel: handleClose, footer: renderFooter(), style: {
            top: 20,
            maxWidth: modalSize.maxWidth,
            minWidth: modalSize.minWidth,
        }, styles: {
            body: {
                height: modalSize.height,
                maxHeight: modalSize.maxHeight,
                minHeight: modalSize.minHeight,
                padding: 0,
                display: "flex",
                flexDirection: "column",
            },
        } }, modalProps, { children: _jsxs("div", __assign({ style: {
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            } }, { children: [searchEnabled && !loading && treeData.length > 0 && (_jsx("div", __assign({ style: {
                        padding: "16px 16px 0 16px",
                        flexShrink: 0,
                    } }, { children: _jsx(Search, { placeholder: searchPlaceholder, value: searchValue, onChange: function (e) { return handleSearch(e.target.value); }, allowClear: true, enterButton: true }) }))), _jsxs("div", __assign({ style: {
                        flex: 1,
                        overflow: "hidden",
                        padding: treeData.length > 0 ? "16px" : "0",
                        display: "flex",
                        flexDirection: "column",
                    } }, { children: [treeData.length > 0 && (_jsx("div", __assign({ style: {
                                flex: 1,
                                overflow: "auto",
                                border: COMPONENT_OUTLINE_BORDER,
                                borderRadius: "6px",
                                padding: "16px",
                            } }, { children: renderContent() }))), treeData.length === 0 && renderContent()] }))] })) })));
};
export default GenericModalTree;
